<script lang="ts">
  import { untrack } from "svelte"
  import WorkflowSummary from "@kontextwerk/web-sdk/ui/layout/WorkflowSummary.svelte"
  import type { WorkflowSummaryModel } from "@kontextwerk/web-sdk/core/layout/workflowSummary"
  import {
    fetchCustomerImportDraft,
    fetchCustomerImportReviewSummary,
    fetchLatestCommittedCustomerImportDraft,
    type CustomerImportDraft,
    type CustomerImportReviewSummary,
  } from "../../../../api/kehrblatt/imports"
  import CustomerImportReviewGroups from "./_modules/customer-import-review-groups"
  import { buildImportDashboardNumbers } from "./_shared/_logic/import-review-projection"

  let {
    draftId = null,
    organizationId = null,
    fallbackToLatestCommitted = true,
    onSummaryChanged = () => {},
  }: {
    draftId?: string | null
    organizationId?: string | null
    fallbackToLatestCommitted?: boolean
    onSummaryChanged?: (summary: CustomerImportReviewSummary | null) => void
  } = $props()

  let draft = $state<CustomerImportDraft | null>(null)
  let summary = $state<CustomerImportReviewSummary | null>(null)
  let loading = $state(false)
  let error = $state<string | null>(null)
  let empty = $state(false)
  let loadedKey = $state<string | null>(null)

  const requestedDraftId = $derived(draftId?.trim() || null)
  const requestedOrganizationId = $derived(organizationId?.trim() || null)
  const cards = $derived(buildImportDashboardNumbers(draft))
  const formatMetricValue = (value: number | string | null | undefined): number | string =>
    typeof value === "number" ? value.toLocaleString("de-DE") : (value ?? "-")
  const summaryModel = $derived.by<WorkflowSummaryModel>(() => {
    const criticalCount = summary?.counts_by_severity?.critical ?? 0

    return {
      eyebrow: "Ergebnis",
      title: "Import-Datenprüfung",
      description:
        "Der Import ist abgeschlossen. Diese Kennzahlen zeigen, was übernommen wurde und welche Punkte noch geprüft werden sollten.",
      metrics: cards.map((card) => ({
        id: card.key,
        label: card.label,
        value: formatMetricValue(card.value),
        tooltipContent: card.tooltipContent ?? undefined,
        tone:
          card.key === "import-completed"
            ? card.value === "Ja"
              ? "success"
              : "warning"
            : card.key === "automatic-cleanup"
              ? "info"
              : "neutral",
      })),
      messages: criticalCount
        ? [
            {
              id: "critical",
              title: "Kritische Punkte vorhanden.",
              body: "Prüfen Sie diese Hinweise, bevor Sie automatische Kontaktaufnahme starten.",
              tone: "danger",
            },
          ]
        : [
            {
              id: "ok",
              title: "Kein kritischer Blocker erkannt.",
              body: "Sie können den Import abschließen und offene Hinweise später weiter prüfen.",
              tone: "success",
            },
          ],
      nextSteps: criticalCount
        ? [
            {
              id: "review-critical",
              label: "Kritische Hinweise in der Datenprüfung öffnen und entscheiden.",
              tone: "danger",
            },
          ]
        : [
            {
              id: "finish",
              label: "Import abschließen oder optionale Hinweise später weiter prüfen.",
              tone: "success",
            },
          ],
    }
  })

  const isNotFoundError = (err: unknown): boolean =>
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    (err as { status?: unknown }).status === 404

  const loadDraftForReview = async (
    key: string | null,
    orgId: string | null,
    shouldFallbackToLatestCommitted: boolean,
  ): Promise<CustomerImportDraft | null> => {
    if (key) {
      try {
        return await fetchCustomerImportDraft(key, orgId)
      } catch (err) {
        if (!shouldFallbackToLatestCommitted || !isNotFoundError(err)) throw err
      }
    }

    return fetchLatestCommittedCustomerImportDraft(orgId)
  }

  const loadReview = async (
    key: string | null,
    orgId: string | null,
    shouldFallbackToLatestCommitted: boolean,
  ) => {
    if (!key && !shouldFallbackToLatestCommitted) {
      loadedKey = "__live_waiting__"
      loading = false
      error = null
      empty = false
      draft = null
      summary = null
      onSummaryChanged(null)

      return
    }

    const loadKey = [
      key ?? "__latest_committed__",
      orgId ?? "__current_org__",
      String(shouldFallbackToLatestCommitted),
    ].join(":")

    if (loadedKey === loadKey) return

    loadedKey = loadKey
    loading = true
    error = null
    empty = false
    draft = null
    summary = null
    onSummaryChanged(null)
    try {
      const loadedDraft = await loadDraftForReview(key, orgId, shouldFallbackToLatestCommitted)

      if (!loadedDraft) {
        draft = null
        summary = null
        onSummaryChanged(null)
        empty = true

        return
      }

      draft = loadedDraft
      summary = await fetchCustomerImportReviewSummary(loadedDraft.id, orgId)
      onSummaryChanged(summary)
    } catch (err) {
      console.error("customer_import_review_load_failed", err)
      error = "Die Import-Datenprüfung konnte nicht geladen werden."
    } finally {
      loading = false
    }
  }

  const refreshSummary = async () => {
    if (!draft) return

    try {
      summary = await fetchCustomerImportReviewSummary(draft.id, requestedOrganizationId)
      onSummaryChanged(summary)
    } catch (err) {
      console.error("customer_import_review_summary_refresh_failed", err)
    }
  }

  $effect(() => {
    const key = requestedDraftId
    const orgId = requestedOrganizationId
    const shouldFallbackToLatestCommitted = fallbackToLatestCommitted
    untrack(() => {
      void loadReview(key, orgId, shouldFallbackToLatestCommitted)
    })
  })
</script>

<section class="import-review">
  <div class="intro">
    <h3>Datenprüfung</h3>
    <p>
      Der Import wurde abgeschlossen. Kehrblatt zeigt hier nur die Punkte, die für spätere
      automatische Kontaktaufnahme oder sichere Zuordnung relevant sein könnten.
    </p>
  </div>

  {#if loading}
    <p class="muted">Import-Ergebnis wird geladen ...</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else if !requestedDraftId && !fallbackToLatestCommitted}
    <div class="empty-state">
      <strong>Daten werden geprüft.</strong>
      <p
        >Das Import-Ergebnis erscheint automatisch, sobald Kehrblatt die Datenprüfung vorbereitet
        hat.</p
      >
    </div>
  {:else if empty}
    <div class="empty-state">
      <strong>Noch kein abgeschlossener Import gefunden.</strong>
      <p>Starten Sie den Import im vorherigen Schritt. Danach erscheint hier die Datenprüfung.</p>
    </div>
  {:else if draft}
    <WorkflowSummary model={summaryModel} surface="light" compact />

    <CustomerImportReviewGroups
      draftId={draft.id}
      organizationId={requestedOrganizationId}
      {summary}
      onChanged={refreshSummary}
    />
  {/if}
</section>

<style lang="less">
  .import-review {
    display: grid;
    gap: 1rem;
    color: var(--text-100);
  }

  .intro {
    h3 {
      margin: 0 0 0.4rem;
      color: var(--text-100);
    }

    p {
      margin: 0;
      color: color-mix(in srgb, var(--text-100) 72%, transparent);
    }
  }

  .empty-state {
    border-radius: 0;
    padding: 0.8rem;
  }

  .empty-state {
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: #fff;
    color: var(--text-invers-100);
  }

  .empty-state strong,
  .empty-state p {
    display: block;
  }

  .empty-state p {
    margin-top: 0.2rem;
  }

  .empty-state p {
    color: color-mix(in srgb, var(--text-invers-100) 72%, transparent);
  }

  .muted {
    color: color-mix(in srgb, var(--text-100) 68%, transparent);
  }

  .error {
    color: #b91c1c;
    font-weight: 700;
  }
</style>
